import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {getDate, getMonth, getYear, parse} from 'date-fns';
import {NavigateFunction, useNavigate} from 'react-router';

import {DEFAULT_CARD} from '@/app/main/onboarding/constants/OnboardingConstants';
import {useAppDispatch} from '@/app/store';
import {E_FORMAT_DATE} from '@/constants/DatesConstants';
import {ECardThemesIds} from '@/dataSource/src/api/cards-theme/entities';
import {useCardsThemeRepository} from '@/dataSource/src/api/cards-theme/useRepository';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import logError from '@/utils/errors/logError';
import {formatDateWithFields} from '@/utils/formatDateWithFields';
import {isDateValid} from '@/utils/validateDates';

import {
  ONBOARDING_TEEN_FIRST_NAME,
  ONBOARDING_TEEN_LAST_NAME,
} from '../../../models';
import {getKidData, saveKidData} from '../../services/handleKidInformation';

import {CYearsOld} from './constants/PersonalInfoConstants';
import {useTrackers} from './trackers';

type ViewModelContextType = {
  selectedTheme: string | undefined;
  firstName: string;
  isSplitDateValid: boolean;
  lastName: string;
  year: string;
  cardThemes: ECardThemesIds[];
  day: string;
  isDisabled: boolean;
  month: string;
  isModalOpen: boolean;
  isMobile: boolean;
  setMonth: (value: string) => void;
  onThemeSelected: (identifier: string) => void;
  setFirstName: (value: string) => void;
  handleContinue: () => void;
  formatDateWithFields: (year: string, month: string, day: string) => string;
  setLastName: (value: string) => void;
  setDay: (value: string) => void;
  setYear: (value: string) => void;
  navigate: NavigateFunction;
  dispatch: ThunkDispatch<any, undefined, UnknownAction> &
    Dispatch<UnknownAction>;
  handleOpenModal: () => void;
  handleCloseModal: () => void;
  handlerDayAndMontOnBlur: () => void;
  isFetchingCardThemes: boolean;
  showFamilyCodeBanner?: boolean;
  handleKidLinked?: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export const ViewModelProvider = ({children}: {children: ReactNode}) => {
  //#region  [---- [PROPERTIES] ----]
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {profile, updateMetadata, refetchProfile} = useUsersRepository();
  const {cardThemes, isFetchingCardThemes} = useCardsThemeRepository();
  const initialTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;
  const initialFirstName =
    localStorage.getItem(ONBOARDING_TEEN_FIRST_NAME) ??
    profile?.metadata?.ONBOARDING_TEEN_FIRST_NAME ??
    '';

  const initialLastName =
    localStorage.getItem(ONBOARDING_TEEN_LAST_NAME) ??
    profile?.metadata?.ONBOARDING_TEEN_LAST_NAME ??
    '';
  const {isMobile} = useWindowSize();
  const kidData = getKidData();
  //#endregion

  //#region  [---- [HOOKS] ----]
  const [selectedTheme, setSelectedTheme] = useState<string>(initialTheme);
  const [firstName, setFirstName] = useState<string>(
    kidData?.firstName || initialFirstName,
  );
  const [lastName, setLastName] = useState<string>(
    kidData?.lastName || initialLastName,
  );
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    if (firstName)
      setFirstName(firstName.charAt(0).toUpperCase() + firstName.slice(1));
  }, [firstName]);

  useEffect(() => {
    if (lastName)
      setLastName(lastName.charAt(0).toUpperCase() + lastName.slice(1));
  }, [lastName]);

  useEffect(() => {
    trackViewAddKidNameDob();
    document.body.style.overflow = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (kidData && kidData?.dob) {
      const birthDate = parse(kidData.dob, E_FORMAT_DATE.yyy_MM_dd, new Date());
      setDay(getDate(birthDate).toString());
      setMonth((getMonth(birthDate) + 1).toString().padStart(2, '0'));
      setYear(getYear(birthDate).toString());
    }
  }, [kidData]);

  useEffect(() => {
    handlerDayAndMontOnBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile?.family?.length > 0) {
      const lastTeenIndex = profile?.family?.length - 1;
      setFirstName(profile.family[lastTeenIndex].first_name || '');
      setLastName(profile.family[lastTeenIndex].last_name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSplitDateValid = useMemo(() => {
    if (!day || !month || !year) return true;
    if (year.length < 4) return true;
    if (!isDateValid(year, month, day)) return false;

    const date = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const maxKidDate = new Date(
      today.getFullYear() - CYearsOld.TeenMaxYearsOld,
      today.getMonth(),
      today.getDate(),
    );

    if (date > today || date < maxKidDate) return false;
    return true;
  }, [day, month, year]);

  const isDisabled = useMemo(() => {
    if (!year || year.length < 4) return true;
    return (
      !firstName || !lastName || !day || !month || !year || !isSplitDateValid
    );
  }, [firstName, lastName, day, month, year, isSplitDateValid]);

  const showFamilyCodeBanner = useMemo(() => {
    return showBanner && !(profile?.family?.length > 0);
  }, [profile?.family?.length, showBanner]);

  const tryRefreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      logError(error, {
        fileName: 'AddKid PersonalInfo ViewModelContext',
        functionName: 'tryRefreshProfile',
      });
    }
  }, [refetchProfile]);

  const handleKidLinked = useCallback(async () => {
    await tryRefreshProfile();
    setShowBanner(false);
  }, [tryRefreshProfile]);
  //#endregion

  //#region  [---- [TRACKER] ----]
  const {
    trackSelectAddKidChangeCardTheme,
    trackViewAddKidCardThemeModal,
    trackSelectAddKidDobContinue,
    trackSelectAddKidNewCardTheme,
    trackViewAddKidNameDob,
  } = useTrackers(selectedTheme);
  //#endregion

  //#region  [---- [LOGIC] ----]
  const handlerDayAndMontOnBlur = useCallback(() => {
    setTimeout(() => {
      if (day.length === 1) {
        setDay(prev => '0' + prev);
      }
      if (month.length === 1) {
        setMonth(prev => '0' + prev);
      }
    }, 300);
  }, [day.length, month.length]);

  const handleOpenModal = useCallback(() => {
    trackViewAddKidCardThemeModal();
    trackSelectAddKidChangeCardTheme();
    setIsModalOpen(true);
  }, [trackSelectAddKidChangeCardTheme, trackViewAddKidCardThemeModal]);

  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const onThemeSelected = useCallback(
    async (identifier: string) => {
      try {
        trackSelectAddKidNewCardTheme();
        await updateMetadata({
          ONBOARDING_CARD_THEME_SELECTED: identifier,
        });
        setSelectedTheme(identifier);
        handleCloseModal();
        await tryRefreshProfile();
      } catch (error) {
        logError(error, {
          customTitle: 'Add Kid - PersonalInfo - Error selecting theme',
          fileName: 'PersonalInfo ViewModelContext',
          functionName: 'onThemeSelect',
          data: {identifier},
        });
      }
    },
    [
      handleCloseModal,
      tryRefreshProfile,
      trackSelectAddKidNewCardTheme,
      updateMetadata,
    ],
  );

  const handleContinue = useCallback(() => {
    saveKidData({
      firstName,
      lastName,
      dob: formatDateWithFields(year, month, day),
    });
    trackSelectAddKidDobContinue();
    if (profile?.family?.length > 0) {
      saveKidData({
        uuid: profile.family[0].uuid,
        phone: profile.family[0].phone,
        email: profile.family[0].email,
        switchAccount: profile.family[0].switch_account || false,
      });
      navigate('/onboarding/add-kid/summary');
    } else {
      navigate('/onboarding/add-kid/contact-info');
    }
  }, [
    day,
    firstName,
    lastName,
    month,
    navigate,
    profile?.family,
    trackSelectAddKidDobContinue,
    year,
  ]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        selectedTheme,
        firstName,
        lastName,
        day,
        month,
        year,
        cardThemes: cardThemes?.themes,
        isDisabled,
        isSplitDateValid,
        isModalOpen,
        isMobile,
        setFirstName,
        setLastName,
        setDay,
        setMonth,
        setYear,
        navigate,
        dispatch,
        handleContinue,
        formatDateWithFields,
        onThemeSelected,
        handleOpenModal,
        handleCloseModal,
        handlerDayAndMontOnBlur,
        isFetchingCardThemes,
        showFamilyCodeBanner,
        handleKidLinked,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
};

export const useViewModelProvider = () => {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error('Personal Info ViewModelProvider is not found.');
  }
  return context;
};
